import data from "@emoji-mart/data";
import { type Editor, Extension, type Range } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { ReactRenderer } from "@tiptap/react";
import SuggestionPlugin, {
  type SuggestionKeyDownProps,
  type SuggestionOptions,
  type SuggestionProps,
} from "@tiptap/suggestion";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import tippy, { type Instance } from "tippy.js";

// Unique plugin key to avoid conflicts with other suggestion plugins
const emojiSuggestionPluginKey = new PluginKey("emojiSuggestion");

interface EmojiItem {
  id: string;
  native: string;
  shortcodes: string;
  name: string;
}

// Extract emoji list from emoji-mart data
const emojiList: EmojiItem[] = Object.values(
  (data as { emojis: Record<string, { id: string; name: string; skins: Array<{ native: string }> }> }).emojis
).map((emoji) => ({
  id: emoji.id,
  native: emoji.skins[0].native,
  shortcodes: emoji.id,
  name: emoji.name,
}));

// Suggestion list component
interface EmojiListProps {
  items: EmojiItem[];
  command: (item: EmojiItem) => void;
}

interface EmojiListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const EmojiList = forwardRef<EmojiListRef, EmojiListProps>(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => setSelectedIndex(0), []);

  // Scroll selected item into view
  useEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: ({ event }) => {
        if (items.length === 0) {
          return false;
        }
        if (event.key === "ArrowUp") {
          setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
          return true;
        }
        if (event.key === "ArrowDown") {
          setSelectedIndex((prev) => (prev + 1) % items.length);
          return true;
        }
        if (event.key === "Enter") {
          // Clamp selectedIndex to valid bounds to handle race condition when items array shrinks
          const safeIndex = Math.min(selectedIndex, items.length - 1);
          const item = items[safeIndex];
          if (item) {
            command(item);
          }
          return true;
        }
        return false;
      },
    }),
    [items, selectedIndex, command]
  );

  return (
    <div className="bg-popover rounded-md shadow-md border p-1 max-h-64 overflow-y-auto">
      {items.length > 0 ? (
        items.map((item, index) => (
          <button
            key={item.id}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            onClick={() => command(item)}
            className={`flex items-center gap-2 w-full px-2 py-1 rounded text-sm text-left ${
              index === selectedIndex ? "bg-accent" : "hover:bg-accent/50"
            }`}
          >
            <span className="text-lg">{item.native}</span>
            <span className="text-muted-foreground">:{item.shortcodes}:</span>
          </button>
        ))
      ) : (
        <div className="px-2 py-1 text-sm text-muted-foreground">No emojis found</div>
      )}
    </div>
  );
});

EmojiList.displayName = "EmojiList";

// Helper to safely get clientRect, providing a fallback when null/undefined
const getClientRect = (clientRect: (() => DOMRect | null) | null | undefined): (() => DOMRect) => {
  return () => clientRect?.() ?? new DOMRect(0, 0, 0, 0);
};

// Suggestion configuration
const suggestion: Omit<SuggestionOptions<EmojiItem>, "editor"> = {
  char: ":",
  items: ({ query }: { query: string }) => {
    return emojiList
      .filter(
        (emoji) =>
          emoji.shortcodes.toLowerCase().includes(query.toLowerCase()) ||
          emoji.name.toLowerCase().includes(query.toLowerCase())
      )
      .slice(0, 10);
  },
  render: () => {
    let component: ReactRenderer<EmojiListRef, EmojiListProps>;
    let popup: Instance[];

    return {
      onStart: (props: SuggestionProps<EmojiItem>) => {
        component = new ReactRenderer(EmojiList, {
          props: props as unknown as EmojiListProps,
          editor: props.editor,
        });

        popup = tippy("body", {
          getReferenceClientRect: getClientRect(props.clientRect),
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "bottom-start",
        });
      },
      onUpdate: (props: SuggestionProps<EmojiItem>) => {
        component.updateProps(props as unknown as EmojiListProps);
        popup[0].setProps({ getReferenceClientRect: getClientRect(props.clientRect) });
      },
      onKeyDown: (props: SuggestionKeyDownProps) => component.ref?.onKeyDown(props) ?? false,
      onExit: () => {
        popup[0].destroy();
        component.destroy();
      },
    };
  },
  command: ({ editor, range, props }: { editor: Editor; range: Range; props: EmojiItem }) => {
    editor.chain().focus().deleteRange(range).insertContent(props.native).run();
  },
};

// The extension
export const EditorExtensionEmojiSuggestion = Extension.create({
  name: "emojiSuggestion",
  addProseMirrorPlugins() {
    return [SuggestionPlugin({ editor: this.editor, pluginKey: emojiSuggestionPluginKey, ...suggestion })];
  },
});
